import qs from 'qs';
import mime from 'mime-types';
import {
  takeLatest, all, put, call,
} from 'redux-saga/effects';
import {
  changePlayerExtraWalletsLoading,
  savePlayerExtraWallets,
  savePlayerWalletHistory,
  savePlayerWalletsInfo,
  savePlayerWalletsInfoV2,
  savePlayerWalletHistoryV2,
} from 'actions/playerWallet/playerWalletActionCreator';
import NetworkService from 'services/networkService';
import { i18n } from 'services';
import PlayerWalletActionType from 'actions/playerWallet/playerWalletActionType';
import {
  changeLoadingState, changeGenerateReportEndedStatus, setErrorMessage,
} from 'app/settings/slices';
import { AppConstants } from 'constants/index';
import { downloadFile, isJSON } from 'utils/utils';
import { getError } from 'utils/helpers';

function* getPlayerWalletHistory({ payload }) {
  try {
    const { params } = payload;
    const options = {
      params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    yield put(changeLoadingState(true));
    const { ReportingAdmin, Wallet, WalletFlowHistory } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Wallet, WalletFlowHistory], options);
    yield put(savePlayerWalletHistory(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* exportPlayerWalletHistory({ payload }) {
  try {
    const options = {
      params: payload.params,
      paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
      responseType: 'blob',
    };
    yield put(changeLoadingState(true));
    const {
      ReportingAdmin, Wallet, WalletFlowHistory, Export,
    } = AppConstants.api;
    const { data, headers } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Wallet, WalletFlowHistory, Export], options);
    downloadFile(data, i18n.t('walletHistory'), mime.extension(headers['content-type']));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerWalletHistoryV2({ payload }) {
  try {
    const { params } = payload;
    const options = {
      params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    yield put(changeLoadingState(true));
    const {
      ReportingAdmin, Wallet, V2, WalletFlowHistory,
    } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Wallet, V2, WalletFlowHistory], options);
    yield put(savePlayerWalletHistoryV2(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* exportPlayerWalletHistoryV2({ payload }) {
  try {
    const options = {
      params: payload.params,
      paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
      responseType: 'blob',
    };
    yield put(changeLoadingState(true));
    const {
      ReportingAdmin, Wallet, V2, WalletFlowHistory, Export,
    } = AppConstants.api;
    const { data, headers } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Wallet, V2, WalletFlowHistory, Export], options);
    downloadFile(data, i18n.t('walletHistory'), mime.extension(headers['content-type']));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerWalletsInfo({ payload }) {
  const { isLoading, id } = payload;
  try {
    if (isLoading) {
      yield put(changeLoadingState(true));
    }

    const { ReportingAdmin, Player, Wallets } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, id, Wallets]);

    if (isLoading) {
      yield put(changeLoadingState(false));
    }

    yield put(savePlayerWalletsInfo(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    if (isLoading) {
      yield put(changeLoadingState(false));
    }
  }
}

function* getPlayerWalletsInfoV2({ payload }) {
  const { isLoading, id } = payload;
  try {
    if (isLoading) {
      yield put(changeLoadingState(true));
    }

    const {
      ReportingAdmin, Player, V2, Wallets,
    } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, V2, id, Wallets]);

    if (isLoading) {
      yield put(changeLoadingState(false));
    }

    yield put(savePlayerWalletsInfoV2(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    if (isLoading) {
      yield put(changeLoadingState(false));
    }
  }
}

function* getPlayerExtraWallets({ payload }) {
  const { id } = payload;
  try {
    yield put(changePlayerExtraWalletsLoading(true));
    const { ReportingAdmin, Wallet, ExtraWallets } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Wallet, id, ExtraWallets]);
    yield put(changePlayerExtraWalletsLoading(false));
    yield put(savePlayerExtraWallets(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerExtraWalletsLoading(false));
  }
}

function* generatePlayersRealWalletsSumReport({ payload }) {
  try {
    const options = {
      params: payload.params,
      paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
    };
    yield put(changeLoadingState(true));
    const {
      ReportingAdmin, Player, PlayerTotals, Generate,
    } = AppConstants.api;
    yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, PlayerTotals, Generate], options);
    yield put(changeGenerateReportEndedStatus(true));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

export default function* playerWalletSaga() {
  yield all([
    takeLatest(PlayerWalletActionType.GET_PLAYER_WALLET_HISTORY, getPlayerWalletHistory),
    takeLatest(PlayerWalletActionType.EXPORT_PLAYER_WALLET_HISTORY, exportPlayerWalletHistory),
    takeLatest(PlayerWalletActionType.GET_PLAYER_WALLETS_INFO, getPlayerWalletsInfo),
    takeLatest(PlayerWalletActionType.GET_PLAYER_WALLETS_INFO_V2, getPlayerWalletsInfoV2),
    takeLatest(PlayerWalletActionType.GET_PLAYER_EXTRA_WALLETS, getPlayerExtraWallets),
    takeLatest(PlayerWalletActionType.GENERATE_PLAYERS_REAL_WALLETS_SUM_REPORT, generatePlayersRealWalletsSumReport),
    takeLatest(PlayerWalletActionType.GET_PLAYER_WALLET_HISTORY_V2, getPlayerWalletHistoryV2),
    takeLatest(PlayerWalletActionType.EXPORT_PLAYER_WALLET_HISTORY_V2, exportPlayerWalletHistoryV2),
  ]);
}
